import {
  type AutogiroFields,
  type BankruptcyFields,
  type ContactInfoFields,
  type DisbursementAccountFields,
  type KycAnswersFields,
} from '@resurs-internal/deposits-app-components';
import { removeKycSession } from 'helpers/kycUtils';
import { type AvailableCountry } from 'models/models';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { CountryContext } from '@/contexts';
import { getSectionMap } from '@/helpers';
import {
  fetchApplication,
  saveApplication,
  submitApplication,
} from '@/services';
import {
  type Application,
  type ApplicationDetailsResponseDTO,
  ApplicationStatus,
  type InterestRateType,
  type OnboardingLinks,
} from '@/types/consumer-deposit-application-service';
import { type KycSurvey } from '@/types/know-your-customer-service';

import { autogiro } from './autogiro';
import { bankruptcy } from './bankruptcy';
import { consents, type ConsentsFields } from './consents';
import { contactInfo } from './contactInfo';
import { disbursementAccount } from './disbursementAccount';
import { kycAnswers } from './kycAnswers';
import { defaultApplication } from './useApplicationUtils';

export type InitialApplication = {
  application: ApplicationDetailsResponseDTO;
  country: AvailableCountry;
  save: (
    updatedFields: Partial<ApplicationDetailsResponseDTO>,
    options?: { skipLoading?: boolean },
  ) => Promise<void>;
};

export type ApplicationFields = {
  autogiroSettings: AutogiroFields;
  bankruptcy: BankruptcyFields;
  campaignCode?: string;
  consents: ConsentsFields;
  contactInfo: ContactInfoFields;
  depositGuaranteeAccepted: boolean;
  disbursementAccount: DisbursementAccountFields;
  kycAnswers: KycAnswersFields;
  productCode?: string;
  productType?: InterestRateType;
  setProductCode: (productCode: string) => void;
  status: ApplicationStatus;
  termsConditionsAccepted: boolean;
};

export type useApplicationFields = {
  application: ApplicationFields;
  isLoading: boolean;
  kycSurvey: KycSurvey;
  links: OnboardingLinks;
  save: (updatedFields: Partial<Application>) => Promise<void>;
  submit: () => Promise<void>;
};

export const useApplication = (applicationId: string): useApplicationFields => {
  const initialLoading = useRef(false);
  const previousApplicationId = useRef<string | undefined>(undefined);
  const previousCountry = useRef<AvailableCountry | undefined>(undefined);
  const navigate = useNavigate();
  const { country } = useContext(CountryContext);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [links, setLinks] = useState<OnboardingLinks>({
    apply_deposit_account: { href: '' },
    autogiro_terms: { href: '' },
    common_terms_deposit_account: { href: '' },
    deposit_guarantee: { href: '' },
  });
  const [application, setApplication] =
    useState<ApplicationDetailsResponseDTO>(defaultApplication());
  const [kycSurvey, setKycSurvey] = useState<KycSurvey>({ groups: [], id: '' });

  useEffect(() => {
    if (
      previousApplicationId.current !== applicationId ||
      previousCountry.current !== country
    ) {
      initialLoading.current = false;
      previousApplicationId.current = applicationId;
      previousCountry.current = country;
    }

    if (applicationId && !initialLoading.current) {
      setLoading(true);
      fetchApplication(applicationId)
        .then((applicationRespose) => {
          if (applicationRespose) {
            setLinks(applicationRespose._links);
            setApplication({
              ...defaultApplication(),
              ...applicationRespose.application,
            });
            setKycSurvey(applicationRespose.kycSurvey as unknown as KycSurvey);
          } else {
            throw new Error('Failed to fetch application');
          }
        })
        .catch(() => navigate('/error'))
        .finally(() => {
          initialLoading.current = true;
          setLoading(false);
        });
    }
  }, [applicationId, navigate, country]);

  const save = async (
    updatedFields: Partial<ApplicationDetailsResponseDTO>,
    options?: { skipLoading?: boolean },
  ) => {
    const updatedApplication = { ...application, ...updatedFields };

    setApplication(updatedApplication);

    if (applicationId && updatedApplication.productCode) {
      if (!options?.skipLoading) setLoading(true);

      return saveApplication(applicationId, updatedApplication)
        .then((value) => {
          if (!value) navigate('/error');
        })
        .finally(() => {
          if (!options?.skipLoading) setLoading(false);
        });
    }
  };

  const setProductCode = (productCode: string) => {
    setApplication((prev) => ({ ...prev, productCode }));
  };

  const submit = async (): Promise<void> => {
    setLoading(true);
    const onSuccessUri = `${window.location.protocol}//${window.location.host}/application/${applicationId}/deposit-account`;
    const onCancelledUri = `${window.location.protocol}//${window.location.host}/application/${applicationId}/summary`;

    if (links?.apply_deposit_account?.href)
      submitApplication(
        links?.apply_deposit_account.href,
        onCancelledUri,
        onSuccessUri,
        application,
        kycSurvey,
        getSectionMap(country, application.interestType),
      )
        .then((response) => {
          switch (response.status) {
            case ApplicationStatus.PENDING:
              navigate('../pending', { state: { code: '200' } });

              break;
            case ApplicationStatus.SENT_FOR_SIGNING:
              if (response._links?.['sign-redirect'].href !== undefined) {
                removeKycSession();
                window.location.assign(response._links?.['sign-redirect'].href);
              }
              break;
            case ApplicationStatus.REJECTED:
              navigate('/denied');
              break;
            default:
              navigate('/error');
          }
        })
        .catch(() => {
          navigate('/error');
        });
    else {
      navigate('/error');
    }
  };

  const InitialApplication = {
    application,
    country,
    save,
  };

  return {
    application: {
      autogiroSettings: autogiro({
        ...InitialApplication,
        autogiroTerm: links.autogiro_terms,
      }),
      bankruptcy: bankruptcy(InitialApplication),
      consents: consents(InitialApplication),
      contactInfo: contactInfo(InitialApplication),
      depositGuaranteeAccepted: application.depositGuaranteeAccepted,
      disbursementAccount: disbursementAccount(InitialApplication),
      kycAnswers: kycAnswers(InitialApplication),
      productCode: application.productCode,
      productType: application.interestType,
      setProductCode,
      status: application.status,
      termsConditionsAccepted: application.termsConditionsAccepted,
    },
    isLoading,
    kycSurvey,
    links,
    save,
    submit,
  };
};
